import { Dispatch, KeyboardEvent } from 'react'
import { WppInput, WppLabel, WppPillGroup, WppPill } from '@wppopen/components-library-react'
import {
  FormReducerAction,
  StateToMultiInputFieldMap,
  StateToTypeMap,
  formHelperMethodFactory,
} from 'reducers/FormReducer/FormReducer'
import { FormReducerMultiTextField } from 'reducers/FormReducer/utils'
import { WppInputCustomEvent } from '@wppopen/components-library/dist/types/components'
import { WARNING_MESSAGES } from 'utils/constants'
import { NoneAddedRender } from '../NoneAddedRender/NoneAddedRender'

export interface KeywordInputProps<T extends keyof StateToTypeMap, Y extends keyof StateToTypeMap> {
  id: StateToMultiInputFieldMap<T>
  inputData?: FormReducerMultiTextField //this is only passed if the object is nested and the id doesn't exist in object top level
  disabled: boolean
  label: string
  formReducer: [StateToTypeMap[Y], Dispatch<FormReducerAction>]
  isKeyword?: boolean
  isRequired?: boolean
  tooltip?: string
  onSubmit?: (id: StateToMultiInputFieldMap<T>, value: string) => void
  onWarning?: (id: StateToMultiInputFieldMap<T>, warning: string | undefined) => void
  onRemovePill?: (id: StateToMultiInputFieldMap<T>, index: number) => void
  onError?: (id: StateToMultiInputFieldMap<T>, error: undefined) => void
}

function KeywordInput<T extends keyof StateToTypeMap, Y extends keyof StateToTypeMap>({
  id,
  inputData,
  label,
  disabled,
  isKeyword,
  formReducer,
  isRequired,
  tooltip,
  onSubmit,
  onWarning,
  onRemovePill,
  onError,
}: KeywordInputProps<T, Y>) {
  const [formReducerState, formReducerDispatch] = formReducer
  const { addInputItem, removeInputItem, handleWarning, handleError } = formHelperMethodFactory<T>()
  const inputField = inputData || (formReducerState[id as keyof StateToTypeMap[Y]] as FormReducerMultiTextField)

  const { value: values, warning, error } = inputField

  // handle keyword inputs
  const handleKeyUp = (e: KeyboardEvent<HTMLWppInputElement>) => {
    const { name, value } = e.currentTarget
    if (!value?.trim() || !name) return
    if (e.key === 'Enter') {
      // give dev freedom to handle submit themselves
      if (onSubmit) onSubmit(id, value)
      else formReducerDispatch(addInputItem(id, value))
      e.currentTarget.value = ''

      // clear warning
      if (warning) {
        if (onWarning) onWarning(id, undefined)
        else formReducerDispatch(handleWarning(id, undefined))
      }

      // clear error
      if (error) {
        if (onError) onError(id, undefined)
        else formReducerDispatch(handleError(id, undefined))
      }
    }
  }

  const handleKeywordWarning = (e: WppInputCustomEvent<FocusEvent>) => {
    const { value } = e.target
    // give dev freedom to handle warning themselves
    if (onWarning) {
      return onWarning(id, !value?.trim() ? undefined : WARNING_MESSAGES.NON_EMPTY_KEYWORD)
    }
    return formReducerDispatch(handleWarning(id, !value?.trim() ? undefined : WARNING_MESSAGES.NON_EMPTY_KEYWORD))
  }

  return (
    <fieldset>
      {!disabled ? (
        <WppInput
          name={id as string}
          labelConfig={{ text: label, description: tooltip, icon: tooltip ? 'wpp-icon-info' : undefined }}
          placeholder={`Type ${isKeyword ? 'keyword' : 'keyphrase'} and press “enter” to add`}
          onKeyUp={handleKeyUp}
          required={isRequired}
          data-testid="keyword-input"
          messageType={warning ? 'warning' : error ? 'error' : undefined}
          message={warning || error}
          onWppBlur={e => handleKeywordWarning(e)}
        />
      ) : (
        <WppLabel
          typography="s-strong"
          config={{
            text: label,
          }}
          disabled={disabled}
          optional
        />
      )}

      {disabled && !values.length && <NoneAddedRender />}
      {!!values.length && (
        <WppPillGroup type="display" value={values} data-testid="keyword-pill-group">
          {values.map((item, index) => (
            <WppPill
              key={item}
              label={item}
              value={item}
              removable={!disabled}
              onWppClose={() => {
                if (onRemovePill) {
                  return onRemovePill(id, index)
                } else {
                  return formReducerDispatch(removeInputItem(id, index))
                }
              }}
              data-testid="keyword-pill"
              disabled={disabled}
            />
          ))}
        </WppPillGroup>
      )}
    </fieldset>
  )
}

export default KeywordInput
